import { MediaObjectUrl, MediaVariantEnum } from '@kudos/http-client'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import { SxProps } from '@mui/material/styles'
import useMemberAdornments from '@shared/hooks/useMemberAdornments'
import { fullNameFromParts, initialsForName } from '@shared/utils/nameFormatting'
import { useT } from '@transifex/react'
import { ReactNode } from 'react'
import AvatarAdornmentBadge from './AvatarAdornmentBadge'
import AvatarWrapper from './AvatarWrapper'
import { getDefaultAvatar } from './defaultAvatars'
import useAvatarConfig from './useAvatarConfig'

export type AvatarSize = number

export type AvatarFontUnit = 'px' | 'vh'

/** Minimum avatar size that shows a milestone adornment badge (medium). */
export const ADORNMENT_MIN_SIZE = 56

type UserAvatarProps = {
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  size?: AvatarSize
  media?: MediaObjectUrl | null
  /* optional override for images that do not have avatar variants */
  mediaVariant?: MediaVariantEnum
  src?: string
  fontUnit?: AvatarFontUnit
  hideDropShadow?: boolean
  defaultIcon?: ReactNode
  avatarBoxStyle?: SxProps
  isActive?: boolean
  /* opt out of native lazy loading for above-the-fold avatars */
  eager?: boolean
  /**
   * Member id used to deterministically pick one of the default avatar images
   * when the member has no synced/uploaded avatar. When omitted, the avatar
   * falls back to initials. Also used to resolve milestone adornments when
   * size is at least ADORNMENT_MIN_SIZE.
   */
  tenantMemberId?: string | null
  /** Suppress the milestone adornment badge even when size/member would otherwise show it. */
  hideAdornment?: boolean
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  firstName,
  lastName,
  fullName,
  size = 40,
  media,
  mediaVariant,
  src,
  fontUnit = 'px',
  hideDropShadow = false,
  defaultIcon,
  avatarBoxStyle = {},
  isActive = true,
  eager = false,
  tenantMemberId,
  hideAdornment = false,
}) => {
  const t = useT()
  const { avatarUrl } = useAvatarConfig(size, media || null, mediaVariant)
  const firstname = fullName?.split(' ')[0] ?? firstName
  const lastname = fullName?.split(' ')[1] ?? lastName
  const adornment = useMemberAdornments(
    !hideAdornment && size >= ADORNMENT_MIN_SIZE ? tenantMemberId : null,
  )
  const showAdornmentBadge = !!adornment && isActive

  // Prefer a real image (explicit src or synced/uploaded media). Only when
  // none is present do we fall back to a deterministic default avatar.
  // We also key off `media` directly (not just the resolved `avatarUrl`)
  // because `useMediaUrl` resolves the variant URL in an effect, so `avatarUrl`
  // is momentarily undefined on the render where `media` first appears. Without
  // this, the member would flash a default avatar before their real photo.
  const hasImage = !!src || !!avatarUrl || !!media?.url
  const defaultAvatar = hasImage ? undefined : getDefaultAvatar(tenantMemberId)
  const resolvedSrc = src ?? defaultAvatar

  const renderAvatarContent = () => {
    if (hasImage || !!defaultAvatar) {
      return null
    }
    return initialsForName(firstname, lastname) || defaultIcon || <PersonOutlineOutlinedIcon />
  }

  return (
    <AvatarWrapper
      size={size}
      media={media}
      src={resolvedSrc}
      alt={t('User profile avatar image')}
      fontUnit={fontUnit}
      hideDropShadow={hideDropShadow}
      eager={eager}
      avatarBoxStyle={{
        ...avatarBoxStyle,
        filter: isActive ? 'none' : 'grayscale(95%)',
        opacity: isActive ? 1 : 0.75,
      }}
      translate="no"
      tooltipTitle={fullName ?? fullNameFromParts(firstName, lastName)}
      adornmentOverlay={
        showAdornmentBadge ? (
          <AvatarAdornmentBadge adornment={adornment} size={size} fontUnit={fontUnit} />
        ) : undefined
      }>
      {renderAvatarContent()}
    </AvatarWrapper>
  )
}

export default UserAvatar
